import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Building2, DollarSign, FileCheck2, Loader2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import Navbar from '@/components/Navbar';
import FCOWizard from '@/components/fco/FCOWizard';
import FCOViewer from '@/components/fco/FCOViewer';
import TransactionChecklist from '@/components/checklist/TransactionChecklist';
import NextStageBanner from '@/components/deal/NextStageBanner';

const DEAL_STATUS_LABEL = {
  pending_verification: 'Pending Verification',
  verified: 'Verified',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export default function DealDetail() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [deal, setDeal] = useState(null);
  const [fco, setFco] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, f, c] = await Promise.all([
        axios.get(`${API_URL}/deals/${dealId}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/deals/${dealId}/fco`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/deals/${dealId}/checklist`, { headers: getAuthHeader() })
          .catch(() => ({ data: null })),  // checklist only exists after ack
      ]);
      setDeal(d.data);
      setFco(f.data);
      setChecklist(c.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load deal');
      navigate('/');
    } finally { setLoading(false); }
  }, [dealId, getAuthHeader, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120]">
        <Navbar />
        <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-[#F59E0B]" /></div>
      </div>
    );
  }
  if (!deal) return null;

  const viewerRole =
    user?.role === 'admin' ? 'admin'
      : user?.id === deal.buyer_id ? 'buyer'
      : user?.id === deal.seller_id ? 'seller'
      : 'unknown';   // unrelated users see nothing — banner + role-gated CTAs return null
  const isSeller = viewerRole === 'seller';
  // Only expose the header CTA when the seller has NO active FCO to draft.
  // Once an FCO exists, the reissue action lives inside FCOViewer to avoid
  // rendering the same button twice.
  const canGenerateFCO = isSeller && deal.status === 'in_progress' && !fco;

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="deal-detail-page">
        <button onClick={() => navigate(-1)}
                data-testid="deal-back-btn"
                className="text-[#64748B] hover:text-white text-sm mb-4 flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <NextStageBanner
          deal={deal}
          fco={fco}
          checklist={checklist}
          viewerRole={viewerRole}
        />

        {/* Deal summary */}
        <Card className="bg-[#121A2F] border-[#1E293B] p-6" data-testid="deal-summary-card">
          <div className="flex justify-between items-start flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#94A3B8]">Deal</p>
              <h1 className="text-2xl text-white font-bold mt-1 font-['Outfit']" data-testid="deal-title">
                {deal.mineral_title}
              </h1>
              <p className="text-xs text-[#64748B] mt-1">ID: {deal.id}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]"
                  data-testid="deal-status-badge">
              {DEAL_STATUS_LABEL[deal.status] || deal.status}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 text-sm">
            <Stat icon={Building2} k="Seller" v={deal.seller_name} />
            <Stat icon={Building2} k="Buyer" v={deal.buyer_name} />
            <Stat icon={Package} k="Quantity" v={`${deal.quantity}`} />
            <Stat icon={DollarSign} k="Total Value"
                  v={`USD ${(deal.total_value || 0).toLocaleString()}`} highlight />
          </div>
        </Card>

        {/* FCO block */}
        <div className="mt-6" data-testid="fco-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-xl text-white font-semibold flex items-center gap-2">
              <FileCheck2 className="w-5 h-5 text-[#F59E0B]" />Full Corporate Offer
            </h2>
            {canGenerateFCO && (
              <Button onClick={() => setWizardOpen(true)}
                      data-testid="generate-fco-btn"
                      className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold">
                <FileCheck2 className="w-4 h-4 mr-1.5" />
                {fco ? 'Issue Revised FCO' : 'Generate FCO'}
              </Button>
            )}
          </div>

          {isSeller && deal.status !== 'in_progress' && !fco && (
            <div className="rounded-md border border-[#F59E0B]/25 bg-[#F59E0B]/[0.06] px-4 py-3 text-xs text-[#94A3B8] mb-3">
              An FCO can be issued once this deal enters <b className="text-white">In Progress</b>.
            </div>
          )}

          <FCOViewer
            dealId={dealId}
            fco={fco}
            onChanged={(next) => { setFco(next); load(); }}
            viewerRole={viewerRole}
            canReissue={isSeller && deal.status === 'in_progress'}
            onReissue={() => setWizardOpen(true)}
          />

          {fco?.status === 'acknowledged' && (
            <TransactionChecklist
              dealId={dealId}
              viewerRole={viewerRole}
              onChanged={setChecklist}
            />
          )}
        </div>

        <FCOWizard
          dealId={dealId}
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          onIssued={setFco}
        />
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, k, v, highlight }) => (
  <div className="rounded-md border border-[#1E293B] bg-white/[0.02] px-3 py-2.5">
    <p className="text-[10px] uppercase tracking-wider text-[#64748B] flex items-center gap-1">
      <Icon className="w-3 h-3" />{k}
    </p>
    <p className={`mt-0.5 ${highlight ? 'text-[#F59E0B] font-semibold' : 'text-white'}`}>{v}</p>
  </div>
);
